import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, X, ChevronDown, ChevronUp, RefreshCw, CheckCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';

interface FeedbackEntry {
  id: number;
  text: string;
  author: string;
  timestamp: string;
  type: 'comment' | 'feature' | 'bug';
  status: 'new' | 'read' | 'in_progress' | 'done';
}

const FeedbackBox: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState<'comment' | 'feature' | 'bug'>('comment');
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'new' | 'read' | 'in_progress' | 'done'>('all');

  // Hent tilbakemeldinger fra Supabase
  const loadFeedback = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('timestamp', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        if (error.message.includes('relation') || error.message.includes('does not exist')) {
          setError('Feedback-tabellen finnes ikke i databasen. Kjør SQL-scriptet først.');
        } else {
          setError('Kunne ikke laste tilbakemeldinger: ' + error.message);
        }
        return;
      }
      
      setEntries(data || []);
    } catch (err) {
      console.error('Feil ved lasting av tilbakemeldinger:', err);
      setError('Kunne ikke laste tilbakemeldinger');
    } finally {
      setIsLoading(false);
    }
  };

  // Last tilbakemeldinger når komponenten mountes
  useEffect(() => {
    loadFeedback();

    // Sett opp realtime subscription
    const channel = supabase
      .channel('feedback_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feedback' },
        (payload) => {
          console.log('Feedback endring mottatt:', payload);
          loadFeedback();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Lagre ny tilbakemelding til Supabase
  const handleSubmit = async () => {
    if (!feedback.trim()) return;

    setIsSaving(true);
    setError(null);

    try {
      const newEntry = {
        text: feedback,
        author: localStorage.getItem('hagia-user-name') || 'Ukjent bruker',
        type: feedbackType,
        status: 'new' as const
      };

      const { error } = await supabase
        .from('feedback')
        .insert([newEntry]);

      if (error) throw error;

      setFeedback('');
      setShowSuccess(true);
      
      await loadFeedback();
      
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Feil ved lagring av tilbakemelding:', err);
      setError('Kunne ikke lagre tilbakemelding. Prøv igjen.');
    } finally {
      setIsSaving(false);
    }
  };

  // Oppdater status på tilbakemelding
  const updateStatus = async (id: number, status: 'new' | 'read' | 'in_progress' | 'done') => {
    try {
      const { error } = await supabase
        .from('feedback')
        .update({ status })
        .eq('id', id);

      if (error) throw error;
      
      setEntries(prev => prev.map(entry => 
        entry.id === id ? { ...entry, status } : entry
      ));
    } catch (err) {
      console.error('Feil ved oppdatering av status:', err);
      setError('Kunne ikke oppdatere status');
    }
  };

  const getTypeLabel = (type: string) => {
    switch(type) {
      case 'feature': return '✨ Ønsket funksjon';
      case 'bug': return '🐛 Feilmelding';
      default: return '💬 Kommentar';
    }
  };

  const getTypeColor = (type: string) => {
    switch(type) {
      case 'feature': return 'bg-blue-100 text-blue-800';
      case 'bug': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'read': return '👁️ Lest';
      case 'in_progress': return '⚙️ Under arbeid';
      case 'done': return '✅ Ferdig';
      default: return '🆕 Ny';
    }
  };

  // Nye verbedrete status-farger (hvit bakgrunn med ramme)
  const getStatusButtonStyle = (status: string, currentStatus: string) => {
    const isActive = status === currentStatus;
    
    switch(status) {
      case 'read':
        return isActive 
          ? 'bg-blue-500 text-white border-blue-500' 
          : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50';
      case 'in_progress':
        return isActive 
          ? 'bg-yellow-500 text-white border-yellow-500' 
          : 'bg-white text-yellow-600 border-yellow-300 hover:bg-yellow-50';
      case 'done':
        return isActive 
          ? 'bg-green-500 text-white border-green-500' 
          : 'bg-white text-green-600 border-green-300 hover:bg-green-50';
      default:
        return isActive 
          ? 'bg-purple-500 text-white border-purple-500' 
          : 'bg-white text-purple-600 border-purple-300 hover:bg-purple-50';
    }
  };

  // Sortering: Under arbeid øverst, deretter nye, lest, og ferdig nederst
  const getSortedEntries = (entries: FeedbackEntry[]) => {
    const statusPriority = {
      'in_progress': 1,
      'new': 2, 
      'read': 3,
      'done': 4
    };

    return [...entries].sort((a, b) => {
      // Først sortér etter status-prioritet
      const aPriority = statusPriority[a.status] || 5;
      const bPriority = statusPriority[b.status] || 5;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      // Deretter sortér etter timestamp (nyeste først innenfor samme status)
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  };

  // Filtrer og sortér entries
  const filteredAndSortedEntries = getSortedEntries(
    filterStatus === 'all' 
      ? entries 
      : entries.filter(e => e.status === filterStatus)
  );

  // Tell antall nye tilbakemeldinger
  const newCount = entries.filter(e => e.status === 'new').length;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg flex items-center space-x-2 transition-all shadow-sm hover:shadow-md"
        title="Legg inn kommentar eller ønske"
      >
        <MessageSquare size={16} />
        <span className="text-sm font-medium">Tilbakemelding</span>
        {newCount > 0 && (
          <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
            {newCount}
          </span>
        )}
      </button>

      {/* Feedback dialog */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
             onClick={() => setIsOpen(false)}>
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b bg-gradient-to-r from-green-50 to-green-100 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="text-green-600" size={20} />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Tilbakemeldinger og ønsker
                  </h3>
                  <span className="text-sm text-gray-600">
                    (Synkronisert via Supabase)
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={loadFeedback}
                    className="text-gray-500 hover:text-gray-700"
                    title="Oppdater"
                  >
                    <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={() => setIsMinimized(!isMinimized)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {isMinimized ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            </div>

            {!isMinimized && (
              <>
                {error && (
                  <div className="p-4 bg-red-100 border-b text-red-700 text-sm">
                    ⚠️ {error}
                  </div>
                )}

                {/* New feedback form */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="space-y-3">
                    <div className="flex space-x-2">
                      {[
                        { value: 'comment', label: '💬 Kommentar', color: 'bg-gray-100' },
                        { value: 'feature', label: '✨ Ønsket funksjon', color: 'bg-blue-100' },
                        { value: 'bug', label: '🐛 Feilmelding', color: 'bg-red-100' }
                      ].map((type) => (
                        <button
                          key={type.value}
                          onClick={() => setFeedbackType(type.value as any)}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            feedbackType === type.value
                              ? 'bg-green-600 text-white'
                              : `${type.color} text-gray-700 hover:bg-opacity-80`
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                    
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Skriv din tilbakemelding her..."
                      className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      rows={3}
                    />
                    
                    <button
                      onClick={handleSubmit}
                      disabled={!feedback.trim() || isSaving}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                    >
                      {isSaving ? (
                        <>
                          <RefreshCw size={16} className="animate-spin" />
                          <span>Lagrer...</span>
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          <span>Send inn</span>
                        </>
                      )}
                    </button>

                    {showSuccess && (
                      <div className="mt-2 p-2 bg-green-100 text-green-700 rounded-lg text-sm">
                        ✅ Takk for tilbakemeldingen! Den er nå synlig for alle brukere.
                      </div>
                    )}
                  </div>
                </div>

                {/* Filter section */}
                <div className="px-4 py-3 border-b bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Vis tilbakemeldinger:
                    </span>
                    <div className="flex space-x-1">
                      {[
                        { value: 'all', label: 'Alle' },
                        { value: 'in_progress', label: 'Under arbeid' },
                        { value: 'new', label: 'Nye' },
                        { value: 'read', label: 'Lest' },
                        { value: 'done', label: 'Ferdig' }
                      ].map((filter) => (
                        <button
                          key={filter.value}
                          onClick={() => setFilterStatus(filter.value as any)}
                          className={`px-3 py-1.5 text-xs rounded transition-colors ${
                            filterStatus === filter.value
                              ? 'bg-green-600 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-100 border'
                          }`}
                        >
                          {filter.label}
                          {filter.value !== 'all' && (
                            <span className="ml-1">
                              ({entries.filter(e => e.status === filter.value).length})
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Entries list */}
                <div className="flex-1 overflow-y-auto p-4">
                  {isLoading ? (
                    <div className="text-center text-gray-500 py-8">
                      <RefreshCw size={32} className="mx-auto mb-3 animate-spin text-gray-400" />
                      <p>Laster tilbakemeldinger...</p>
                    </div>
                  ) : filteredAndSortedEntries.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <MessageSquare size={48} className="mx-auto mb-3 text-gray-300" />
                      <p>
                        {filterStatus === 'all' 
                          ? 'Ingen tilbakemeldinger ennå.'
                          : `Ingen tilbakemeldinger med valgt status.`}
                      </p>
                      {filterStatus === 'all' && (
                        <p className="text-sm mt-1">Vær den første til å legge inn en!</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredAndSortedEntries.map((entry) => (
                        <div key={entry.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-2 flex-wrap">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(entry.type)}`}>
                                {getTypeLabel(entry.type)}
                              </span>
                              <span className="text-sm font-medium text-gray-700">
                                {entry.author}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(entry.timestamp).toLocaleString('nb-NO')}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">
                            {entry.text}
                          </p>
                          
                          {/* Status-knapper med hvit bakgrunn og ramme */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => updateStatus(entry.id, 'read')}
                              className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-all font-medium ${getStatusButtonStyle('read', entry.status)}`}
                              title="Marker som lest"
                            >
                              👁️ Lest
                            </button>
                            <button
                              onClick={() => updateStatus(entry.id, 'in_progress')}
                              className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-all font-medium ${getStatusButtonStyle('in_progress', entry.status)}`}
                              title="Marker som under arbeid"
                            >
                              ⚙️ Under arbeid
                            </button>
                            <button
                              onClick={() => updateStatus(entry.id, 'done')}
                              className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-all font-medium ${getStatusButtonStyle('done', entry.status)}`}
                              title="Marker som ferdig"
                            >
                              ✅ Ferdig
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer info */}
                <div className="p-3 bg-gray-50 border-t text-center">
                  <p className="text-xs text-gray-600">
                    📡 Alle tilbakemeldinger synkroniseres automatisk mellom alle brukere via Supabase.
                    {entries.length > 0 && (
                      <span className="block mt-1">
                        Totalt {entries.length} tilbakemeldinger i systemet.
                      </span>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackBox;